// Report columns — the model behind the "Columns" chooser on the time reports
// and the Time column those reports print.  Firm request (2026-10-15): on
// every time report, let each person turn columns (Rate, Amount, Inv, …) on
// and off; what they choose applies to the on-screen table, the PDF, 🖨️ Print
// and Excel alike, and is remembered per report per person.
//
// Pure: no React, no api, no jsPDF.  The chooser itself is
// components/ReportColumnChooser.js; Reports.js and lib/matterPDF.js decide
// how each output draws the visible columns.
//
// Storage: `lextrack_report_columns_<reportId>` holds the HIDDEN column keys
// (JSON array).  Hidden, not visible: a column a report gains later (the Time
// column, say) then shows up switched on, and an empty / missing / unreadable
// entry means the default set — every column on.  Registered in
// lib/uiPrefs.js ("Sort + filter preferences").  Every read and write is
// wrapped: storage can throw (private window, blocked site data) and the
// page must still work, with every column showing.

export const REPORT_COLUMNS_KEY_PREFIX = 'lextrack_report_columns_'

const storageKey = (reportId) => `${REPORT_COLUMNS_KEY_PREFIX}${reportId}`

// The hidden keys saved for a report, or [] (the default set).
export function loadHiddenColumns (reportId) {
  if (!reportId) return []
  try {
    const raw = localStorage.getItem(storageKey(reportId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return [...new Set(parsed.filter(k => typeof k === 'string' && k))]
  } catch {
    return []
  }
}

// Saves the hidden keys; an empty list removes the entry (default set).
// Returns false when storage refused it — the choice still applies until the
// page is reloaded.
export function saveHiddenColumns (reportId, hidden) {
  if (!reportId) return false
  try {
    const list = [...new Set((hidden || []).filter(k => typeof k === 'string' && k))]
    if (list.length === 0) localStorage.removeItem(storageKey(reportId))
    else localStorage.setItem(storageKey(reportId), JSON.stringify(list))
    return true
  } catch {
    return false
  }
}

// The columns to draw, in the report's own order.  At least one column always
// stays on: if every column is hidden (an old saved choice for a report whose
// columns changed), the whole set shows rather than an empty table.
export function visibleColumnKeys (allKeys, hidden) {
  const all = Array.isArray(allKeys) ? allKeys : []
  const off = new Set(Array.isArray(hidden) ? hidden : [])
  const on = all.filter(k => !off.has(k))
  return on.length ? on : all.slice()
}

// Turn one column on or off.  Refuses (returns the same list) to hide the
// last visible column.  Keys not in `allKeys` are kept as they are, so a
// choice made while one report's rows lacked a column survives.
export function toggleHiddenColumn (allKeys, hidden, key) {
  const cur = Array.isArray(hidden) ? hidden : []
  if (cur.includes(key)) return cur.filter(k => k !== key)
  const visible = visibleColumnKeys(allKeys, cur)
  if (!visible.includes(key) || visible.length <= 1) return cur
  return [...cur, key]
}

// The chooser's label for a generic Reports-page column key: the same words
// the on-screen header shows ("file_number" → "File number"), with ID kept
// upper case.  The two matter reports label their columns themselves
// (matterTimeReports.columnsFor().header).
const CHOOSER_LABELS = { id: 'ID', matter_id: 'Matter ID', user_id: 'User ID' }
export function columnChooserLabel (key) {
  if (CHOOSER_LABELS[key]) return CHOOSER_LABELS[key]
  const words = String(key || '').replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : ''
}

// ── Footer totals ──────────────────────────────────────────────────────────

// An identifier column (id, matter_id, user_id) holds numbers that must never
// be added up — once a column to its left is hidden it would otherwise land
// in a TOTAL row as a meaningless sum of ids.
export const isIdColumn = (key) => key === 'id' || /_id$/.test(String(key || ''))

// ── The Time column ────────────────────────────────────────────────────────
//
// `start_time` is the firm-local wall-clock time an entry started
// (time_entries.start_time, migrations_time_entry_start_time.sql).
// /api/time-entries and the time_by_attorney_detail aggregate send 'HH:MM'
// or null; routes that return `te.*` (e.g. GET /api/matters/:id) send
// Postgres TIME text, 'HH:MM:SS'.  Entries saved without one (every row from
// before 2026-10-15, imports) are null, so their Time is blank — never
// "Invalid Date", never a guess.

const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/

// 'HH:MM' (zero-padded, 24-hour) for sorting, or '' when absent / unreadable.
export function normalizeStartTime (v) {
  if (v == null) return ''
  const m = String(v).trim().match(TIME_RE)
  if (!m) return ''
  const h = Number(m[1])
  const min = Number(m[2])
  const s = m[3] == null ? 0 : Number(m[3])
  if (h > 23 || min > 59 || s > 59) return ''
  return `${String(h).padStart(2, '0')}:${m[2]}`
}

// '10:15 AM' / '12:05 PM' / '12:00 AM', or '' (blank) when absent.
export function formatStartTime (v) {
  const n = normalizeStartTime(v)
  if (!n) return ''
  const h = Number(n.slice(0, 2))
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${n.slice(3)} ${h < 12 ? 'AM' : 'PM'}`
}

// Within one day: by start time, entries with no time last, then by id.
// `dateOf` / `timeOf` / `idOf` read the row; `dateDir` keeps a report's own
// day order ('asc' oldest first, 'desc' newest first).
export function compareDayTimeId (a, b, { dateOf, timeOf, idOf, dateDir = 'asc' } = {}) {
  const da = String(dateOf(a) ?? '')
  const db = String(dateOf(b) ?? '')
  if (da !== db) return (da < db ? -1 : 1) * (dateDir === 'desc' ? -1 : 1)
  const ta = normalizeStartTime(timeOf(a))
  const tb = normalizeStartTime(timeOf(b))
  if (ta !== tb) {
    if (!ta) return 1
    if (!tb) return -1
    return ta < tb ? -1 : 1
  }
  const ia = Number(idOf(a))
  const ib = Number(idOf(b))
  if (Number.isFinite(ia) && Number.isFinite(ib) && ia !== ib) return ia - ib
  return 0
}

// The date part of a pg DATE as it arrives ("2026-10-05" or
// "2026-10-05T00:00:00.000Z"), for comparing days without a Date object.
export const dayKey = (d) => String(d ?? '').slice(0, 10)
